import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { MatchRole, resolveEnum } from '@/models/tournament';
import type { MatchView, RefereeView } from '@/models/tournament';

interface AssignmentActionPanelProps {
  selectedMatch: MatchView | null;
  selectedReferee: RefereeView | null;
  onAssign: () => void;
}

const AssignmentActionPanel: React.FC<AssignmentActionPanelProps> = ({ selectedMatch, selectedReferee, onAssign }) => {
  const { t, i18n } = useTranslation('tournament');

  const roleKeys: Record<MatchRole, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const canAssign = selectedMatch && selectedReferee;

  // Check for conflicts
  const hasConflict = false; // Simplified: real conflict checking done server-side

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-4" data-testid="assignment-action-panel">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
          <Icon name="CheckSquare" size={20} className="me-2 text-primary" />
          {t('Assignment Actions')}
        </h2>

        {!selectedMatch && !selectedReferee && (
          <div className="text-center py-8">
            <Icon name="MousePointerClick" size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-600 mb-1">{t('Select a match and referee')}</p>
            <p className="text-xs text-gray-500">{t('to proceed with assignment')}</p>
          </div>
        )}

        {selectedMatch && !selectedReferee && (
          <div className="text-center py-8">
            <Icon name="User" size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-600 mb-1">{t('Match selected')}</p>
            <p className="text-xs text-gray-500">{t('Now select a referee to assign')}</p>
          </div>
        )}

        {!selectedMatch && selectedReferee && (
          <div className="text-center py-8">
            <Icon name="Calendar" size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-600 mb-1">{t('Referee selected')}</p>
            <p className="text-xs text-gray-500">{t('Now select a match to assign')}</p>
          </div>
        )}

        {canAssign && (
          <div className="space-y-4">
            {/* Assignment Preview */}
            <div className="bg-gradient-to-br from-primary/5 to-blue-50 border border-primary/20 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-700 mb-3">{t('Assignment Preview')}</p>

              {/* Match Info */}
              <div className="bg-white rounded-lg p-3 mb-3">
                <div className="flex items-center mb-2">
                  <Icon name="Calendar" size={14} className="text-primary me-2" />
                  <p className="text-xs font-medium text-gray-700">{t('Match')}</p>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {selectedMatch?.team1Name} {t('vs')} {selectedMatch?.team2Name}
                </p>
                <p className="text-xs text-gray-600">{selectedMatch?.id}</p>
                <div className="flex items-center mt-2 text-xs text-gray-600">
                  <Icon name="Clock" size={12} className="me-1" />
                  <span>{new Date(selectedMatch?.scheduledDateTime ?? '')?.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}</span>
                </div>
              </div>

              {/* Referee Info */}
              <div className="bg-white rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <Icon name="User" size={14} className="text-primary me-2" />
                  <p className="text-xs font-medium text-gray-700">{t('Referee')}</p>
                </div>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  {selectedReferee?.photoUrl && (
                    <img
                      src={selectedReferee?.photoUrl}
                      alt={`${selectedReferee?.name} profile photo`}
                      className="w-10 h-10 rounded-full object-cover border-2 border-border"
                    />
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{selectedReferee?.name}</p>
                    <p className="text-xs text-gray-600">{String(selectedReferee?.certification)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Conflict Detection */}
            {hasConflict && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start">
                  <Icon name="AlertCircle" size={14} className="text-red-600 me-2 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-red-900 mb-1">{t('Scheduling Conflict Detected')}</p>
                    <p className="text-xs text-red-700">
                      {t('Referee has another match scheduled on this date')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Existing Referee Team Members */}
            {selectedMatch?.refereeTeamMembers?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start">
                  <Icon name="Users" size={14} className="text-amber-600 me-2 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-amber-900 mb-1">{t('Existing Referee Team Members')}</p>
                    <div className="space-y-1">
                      {selectedMatch.refereeTeamMembers.map((member) => (
                        <p key={member.id} className="text-xs text-amber-700">
                          {member.userName} ({t(roleKeys[(resolveEnum(MatchRole, member.role) as MatchRole) ?? member.role] ?? 'Unknown')})
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button
                onClick={onAssign}
                disabled={hasConflict}
                className="w-full"
                iconName="CheckCircle"
                data-testid="assign-referee-btn"
              >
                {t('Assign Referee')}
              </Button>

              {hasConflict && (
                <p className="text-xs text-center text-red-600">
                  {t('Cannot assign due to scheduling conflict')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {selectedReferee && (
        <div className="bg-card border border-border rounded-lg p-4" data-testid="referee-stats-panel">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <Icon name="BarChart3" size={16} className="me-2 text-primary" />
            {t('Referee Stats')}
          </h3>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon name="Award" size={14} className="text-gray-400 me-2" />
                <span className="text-xs text-gray-600">{t('Total Matches')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{selectedReferee?.totalMatches}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon name="Star" size={14} className="text-yellow-500 me-2" />
                <span className="text-xs text-gray-600">{t('Performance Rating')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{selectedReferee?.rating}/5.0</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon name="MapPin" size={14} className="text-gray-400 me-2" />
                <span className="text-xs text-gray-600">{t('Location')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{selectedReferee?.location}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentActionPanel;
